import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Spin,
  Typography,
} from "antd";
import {
  ReloadOutlined,
  RobotOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";
import type { Booking } from "../../types/booking";
import type { CommercialInvoiceAnalysisResult } from "./types";
import {
  analyzeCommercialInvoiceFile,
  analyzeStoredCommercialInvoice,
  fetchCachedCommercialInvoiceAnalysis,
} from "./commercialInvoiceAnalysisService";
import {
  getCachedAnalysisForPath,
} from "./commercialInvoiceAnalysisCache";
import { CommercialInvoiceAnalysisDetailModal } from "./CommercialInvoiceAnalysisDetailModal";
import { CommercialInvoiceDeepCheckModal } from "./CommercialInvoiceDeepCheckModal";
import { getCommercialInvoiceValidityBreakdown } from "./commercialInvoiceValidity";
import { CommercialInvoiceValidityDisplay } from "./CommercialInvoiceValidityDisplay";

const { Text } = Typography;

export interface CommercialInvoiceAnalysisPanelProps {
  bookingId: number;
  /** Booking record for cross-check against wizard fields. */
  booking?: Booking | null;
  /** Stored path — analyzes on mount when set. */
  objectPath?: string | null;
  /** Local file — analyze when set (e.g. before upload). */
  file?: File | null;
  autoRun?: boolean;
  compact?: boolean;
  /** Called after a fresh analysis is saved (not when loading cache). */
  onAnalysisUpdated?: () => void;
}

export const CommercialInvoiceAnalysisPanel: React.FC<
  CommercialInvoiceAnalysisPanelProps
> = ({
  bookingId,
  booking,
  objectPath,
  file,
  autoRun = true,
  compact = false,
  onAnalysisUpdated,
}) => {
  const [result, setResult] = useState<CommercialInvoiceAnalysisResult | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [deepCheckOpen, setDeepCheckOpen] = useState(false);
  const [deepCheckLoading, setDeepCheckLoading] = useState(false);

  const loadAnalysis = useCallback(
    async (options?: { forceRefresh?: boolean; deepCheck?: boolean }) => {
      if (!objectPath && !file) return;

      const isDeepCheck = options?.deepCheck === true;
      if (isDeepCheck) {
        setDeepCheckLoading(true);
      } else {
        setLoading(true);
      }
      setError(null);

      try {
        if (
          objectPath &&
          !file &&
          !options?.forceRefresh &&
          !isDeepCheck
        ) {
          const { data: cachedRow } = await fetchCachedCommercialInvoiceAnalysis(
            bookingId
          );
          const cached = getCachedAnalysisForPath(cachedRow, objectPath);
          if (cached) {
            setResult(cached);
            return;
          }
        }

        const response = file
          ? await analyzeCommercialInvoiceFile(
              { bookingId, file },
              { deepCheck: isDeepCheck }
            )
          : await analyzeStoredCommercialInvoice(
              { bookingId, objectPath: objectPath! },
              {
                deepCheck: isDeepCheck,
                forceRefresh: options?.forceRefresh ?? false,
              }
            );

        if (response.error || !response.data) {
          setError(
            response.error ??
              (isDeepCheck ? "AI deep check failed" : "Analysis failed")
          );
          if (!isDeepCheck) {
            setResult(null);
          }
        } else {
          setResult(response.data);
          if (isDeepCheck) {
            if (response.data.deepCheck?.available) {
              setDeepCheckOpen(true);
            } else {
              setError(response.data.deepCheck?.error ?? "AI deep check unavailable");
            }
          }
          onAnalysisUpdated?.();
        }
      } finally {
        if (isDeepCheck) {
          setDeepCheckLoading(false);
        } else {
          setLoading(false);
        }
      }
    },
    [bookingId, file, objectPath]
  );

  const runAnalysis = useCallback(
    () => loadAnalysis({ forceRefresh: true }),
    [loadAnalysis]
  );

  const runDeepCheck = useCallback(() => {
    if (result?.deepCheck?.available) {
      setDeepCheckOpen(true);
      return;
    }
    loadAnalysis({ deepCheck: true });
  }, [loadAnalysis, result?.deepCheck?.available]);

  useEffect(() => {
    if (!autoRun) return;
    if (objectPath || file) {
      loadAnalysis();
    }
  }, [autoRun, objectPath, file, loadAnalysis]);

  if (!objectPath && !file) return null;

  if (loading && !result) {
    return (
      <div style={{ textAlign: "center", padding: compact ? 8 : 16 }}>
        <Spin size="small" />
        <Text type="secondary" style={{ display: "block", marginTop: 8, fontSize: 12 }}>
          Analysing invoice…
        </Text>
      </div>
    );
  }

  if (error && !result) {
    return (
      <Alert
        type="warning"
        showIcon
        message="Could not analyse invoice"
        description={error}
        action={
          <Button size="small" onClick={runAnalysis}>
            Retry
          </Button>
        }
      />
    );
  }

  if (!result) return null;

  const validity = getCommercialInvoiceValidityBreakdown(result, booking);
  const hasSavedDeepCheck = Boolean(result.deepCheck?.available);

  return (
    <>
      <div
        style={{
          border: "1px solid #f0f0f0",
          borderRadius: 8,
          padding: compact ? 8 : 10,
          background: "#fafafa",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <CommercialInvoiceValidityDisplay breakdown={validity} compact showAiNote />
          </div>
          <Button
            size="small"
            icon={<ReloadOutlined />}
            loading={loading}
            onClick={runAnalysis}
            title="Re-analyse"
            aria-label="Re-analyse"
          >
            {compact ? null : "Re-analyse"}
          </Button>
        </div>

        <Button
          block
          size="small"
          icon={<UnorderedListOutlined />}
          onClick={() => setDetailsOpen(true)}
          style={{ marginTop: 8 }}
        >
          Details & issues
        </Button>

        <Button
          block
          size="small"
          icon={<RobotOutlined />}
          loading={deepCheckLoading}
          onClick={runDeepCheck}
          style={{ marginTop: 6 }}
        >
          {hasSavedDeepCheck ? "View AI deep check" : "Run AI deep check"}
        </Button>
      </div>

      <CommercialInvoiceAnalysisDetailModal
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        result={result}
        booking={booking}
        loading={loading}
        onReanalyse={runAnalysis}
        onOpenDeepCheck={
          hasSavedDeepCheck ? () => setDeepCheckOpen(true) : undefined
        }
      />

      {result.deepCheck ? (
        <CommercialInvoiceDeepCheckModal
          open={deepCheckOpen}
          onClose={() => setDeepCheckOpen(false)}
          deepCheck={result.deepCheck}
        />
      ) : null}
    </>
  );
};
